import { type Subscription } from "@/types/subscription";

export type SortOption = "nextPayment" | "amount" | "name" | "status";

/**
 * Gets tag names for a subscription
 */
export const getSubscriptionTagNames = (
  subscriptionId: string,
  tagsBySubscriptionId: Record<string, string[]>,
  categories: Array<{ id: string; name: string }>,
): string[] => {
  return (tagsBySubscriptionId[subscriptionId] || [])
    .map((id: string) => categories.find((c) => c.id === id)?.name)
    .filter(Boolean) as string[];
};

/**
 * Filters subscriptions based on search, status, and category criteria
 */
export const filterSubscriptions = (
  subscriptions: Subscription[],
  searchTerm: string,
  filterStatus: string,
  filterCategories: string | string[], // Support both single string (legacy) and array
  tagsBySubscriptionId: Record<string, string[]>,
  categories: Array<{ id: string; name: string }>,
): Subscription[] => {
  const search = searchTerm.toLowerCase();

  // Normalize filterCategories to always be an array
  const categoryFilters = Array.isArray(filterCategories)
    ? filterCategories
    : filterCategories === "all"
      ? []
      : [filterCategories];

  return subscriptions.filter((subscription) => {
    const tagNames = getSubscriptionTagNames(
      subscription.id,
      tagsBySubscriptionId,
      categories,
    );

    // Search match criteria
    const matchesSearch =
      search === "" ||
      subscription.name.toLowerCase().includes(search) ||
      subscription.category.toLowerCase().includes(search) ||
      tagNames.some((name) => name.toLowerCase().includes(search));

    // Status filter
    const matchesStatus =
      filterStatus === "all" || subscription.status === filterStatus;

    // Category filter - match if no filters selected OR subscription matches any filter
    const matchesCategories =
      categoryFilters.length === 0 ||
      categoryFilters.includes(subscription.category) ||
      categoryFilters.some((filter) => tagNames.includes(filter));

    return matchesSearch && matchesStatus && matchesCategories;
  });
};

/**
 * Sorts subscriptions based on the given sort criteria
 */
export const sortSubscriptions = (
  subscriptions: Subscription[],
  sortBy: SortOption,
): Subscription[] => {
  return [...subscriptions].sort((a, b) => {
    switch (sortBy) {
      case "nextPayment":
        return (
          new Date(a.nextPayment).getTime() - new Date(b.nextPayment).getTime()
        );
      case "amount":
        return b.amount - a.amount;
      case "name":
        return a.name.localeCompare(b.name);
      case "status":
        return a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });
};

/**
 * Gets unique category names from categories and tags
 */
export const getUniqueCategories = (
  categories: Array<{ id: string; name: string }>,
  tagsBySubscriptionId: Record<string, string[]>,
): string[] => {
  const categoryNames = categories.map((cat) => cat.name);

  const tagLabels = new Set<string>();
  Object.values(tagsBySubscriptionId).forEach((ids) => {
    ids.forEach((id) => {
      const name = categories.find((c) => c.id === id)?.name;
      if (name) tagLabels.add(name);
    });
  });

  return Array.from(
    new Set([...categoryNames, ...Array.from(tagLabels).sort()]),
  );
};
